import Phaser from 'phaser';
import type { Direction } from '../types';

export class PlayerPacket extends Phaser.GameObjects.Container {
  private readonly core: Phaser.GameObjects.Arc;
  private readonly shell: Phaser.GameObjects.Arc;
  private readonly glow: Phaser.GameObjects.Arc;
  private readonly shield: Phaser.GameObjects.Arc;
  private readonly lock: Phaser.GameObjects.Text;
  private readonly mouth: Phaser.GameObjects.Arc;
  private readonly trail: Phaser.GameObjects.Triangle;

  constructor(scene: Phaser.Scene, x: number, y: number, reducedMotion: boolean) {
    super(scene, x, y);
    scene.add.existing(this);
    this.setDepth(30);
    this.glow = scene.add.circle(0, 0, 42, 0x1ed8ff, 0.18);
    this.trail = scene.add.triangle(-43, 0, 0, -14, 0, 14, -27, 0, 0xff8a34, 0.95);
    this.shell = scene.add.circle(0, 0, 31, 0xffffff, 1).setStrokeStyle(6, 0x1ed8ff, 1);
    this.core = scene.add.circle(0, 3, 22, 0x168cff, 1);
    const brow = scene.add.rectangle(0, -14, 24, 3, 0x06152f, 0.22);
    const leftEye = scene.add.circle(-8, -7, 3.5, 0x06152f, 1);
    const rightEye = scene.add.circle(8, -7, 3.5, 0x06152f, 1);
    this.mouth = scene.add.arc(0, 5, 8, 8, 172, false, 0x000000, 0).setStrokeStyle(2.6, 0xffffff, 1);
    this.shield = scene.add.circle(0, 0, 41, 0xff8a34, 0.08).setStrokeStyle(4, 0xffb37a, 1).setVisible(false);
    this.lock = scene.add.text(0, 0, '▣', { fontFamily: 'Arial Black, Arial', fontSize: '27px', color: '#ffffff' }).setOrigin(0.5).setVisible(false);
    this.add([this.glow, this.trail, this.shell, this.core, brow, leftEye, rightEye, this.mouth, this.shield, this.lock]);
    if (!reducedMotion) {
      scene.tweens.add({ targets: this.glow, scale: 1.25, alpha: 0.04, duration: 720, yoyo: true, repeat: -1 });
      scene.tweens.add({ targets: this, scaleY: 1.035, duration: 460, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
    }
  }

  face(direction: Direction): void {
    const rotations: Record<Direction, number> = { right: 0, down: Math.PI / 2, left: Math.PI, up: -Math.PI / 2 };
    this.setRotation(rotations[direction]);
  }

  reactCollect(): void {
    this.scene.tweens.add({ targets: this, scaleX: 1.2, scaleY: 0.9, duration: 75, yoyo: true, ease: 'Quad.Out' });
  }

  reactHit(): void {
    this.mouth.setStartAngle(190).setEndAngle(350);
    this.scene.tweens.add({ targets: this, alpha: 0.25, duration: 80, yoyo: true, repeat: 4 });
  }

  celebrate(): void {
    this.mouth.setStartAngle(8).setEndAngle(172);
    this.scene.tweens.add({ targets: this, angle: 360, scale: 1.38, duration: 620, ease: 'Back.Out' });
  }

  connectionLost(): void {
    this.reactHit();
    this.scene.tweens.add({ targets: this, angle: '+=40', duration: 140, yoyo: true, repeat: 2 });
  }

  setProtected(active: boolean): void {
    this.shield.setVisible(active);
    this.core.setFillStyle(active ? 0xff8a34 : 0x168cff, 1);
  }

  setEncrypted(active: boolean): void {
    this.lock.setVisible(active);
    this.shell.setAlpha(active ? 0.72 : 1);
  }

  setBoosted(active: boolean): void {
    this.trail.setScale(active ? 1.55 : 1).setAlpha(active ? 1 : 0.95);
  }
}
